import { Directive, computed, input } from '@angular/core';
import {
  PULLQUOTE_SIZES,
  PULLQUOTE_VARIANTS,
  type PullquoteSize,
  type PullquoteVariant,
} from '@groove/core';

// The axis vocabularies are owned by @groove/core (framework-neutral, so the
// coverage engine can read them without pulling in @angular/core). Re-exported
// here so consumers can keep importing them from the directive.
export {
  PULLQUOTE_SIZES,
  PULLQUOTE_VARIANTS,
  type PullquoteSize,
  type PullquoteVariant,
};

/**
 * Editorial pull-quote (component tier). Host is a `<figure>` grouping a
 * `<blockquote>` (the quote) and an optional `<figcaption>` (the attribution) —
 * the semantically strongest, axe-favoured form (figure scopes the quote +
 * caption as one unit). A bare `<blockquote>` host is also accepted for a quote
 * without attribution.
 *
 * Variants (scene-agnostic by construction; distinguished by how the quote is
 * set off — the consumer picks which structure fits):
 *   - floating (default) — a giant leading quote mark in the display face
 *     (decor-soft ornament), no container. The editorial showcase form.
 *   - boxed — a filled panel with a left rule, no giant mark. A contained form.
 *   - rule — a left rule + padding only, no mark, no panel. The lightest form;
 *     keeps the page surface.
 *   - card — a surface-backed card (bg + soft elevation + radius) with a small
 *     leading quote glyph, no rule. The surface, elevation, radius, and glyph
 *     size are consumer-overridable via --_pullquote-bg / -shadow / -radius /
 *     -mark-size, so a consumer skins the card without the component carrying
 *     any brand.
 *
 * Size facet (named tiers, not per-instance raw sizes — keeps the quote
 * vocabulary consistent across a consumer):
 *   - feature (default) — the large editorial quote (typically beside a photo).
 *   - md — inline attributed testimonials.
 *   - sm — dense grid tiles.
 *
 * Per-instance LAYOUT overrides (margins, centering) still ride --_pullquote-*
 * / instance classes; SIZE is the facet, not a raw token override.
 */
@Directive({
  selector: '[groovePullquote]',
  standalone: true,
  host: {
    class: 'groove-pullquote',
    '[attr.data-variant]': 'variantAttr()',
    '[attr.data-size]': 'sizeAttr()',
  },
})
export class GroovePullquoteDirective {
  readonly variant = input<PullquoteVariant>('floating');
  readonly size = input<PullquoteSize>('feature');

  // Omit the default attr to keep the DOM clean (omit-default-attribute pattern).
  protected readonly variantAttr = computed(() =>
    this.variant() === 'floating' ? null : this.variant(),
  );
  protected readonly sizeAttr = computed(() =>
    this.size() === 'feature' ? null : this.size(),
  );
}
